import React, { useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton
} from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import DataApi from 'api/faber/DataApi'
import MenuClose from 'assets/icons/MenuClose.png'
import MUIDataGrid from 'common/AbstractDataGrid/DataGridMUI'
import TextColorFontHeader from 'common/Typography/TextColorFontHeader'
import { generateColumnHeader } from 'helpers/column-generator/generateColumnHeader'
import { DataCDRPackageDetailType } from 'types/api/faber/dataUsage.type'

import { PaperComponent } from '../sharedDialogs/UpdateFaberUserDetailsDialog'

const CustomerUsageCDRBreakdown = ({
  handleDialogClose,
  breakdownDialogOpen,
  customerICCID,
  faberToken,
  isExternal
}: DataCDRProps) => {
  /*FOR DATA GRID */
  const [detailedRows, setDetailedRows] = useState<DataCDRPackageDetailType[]>(
    []
  )

  const usageColumns = useMemo(
    () => generateColumnHeader('customerUsageBreakdown'),
    []
  )

  /*API CALL TO GET DATA*/
  const { isLoading: isDetailedCDRLoading, refetch: refetchDetailCDRData } =
    useQuery(
      ['getDetailDataCDR', customerICCID],
      async () => {
        const getCDRDataDetailsResponse =
          await 
          (isExternal ? 
            DataApi.getExternalDataCDRDetailedRecords(customerICCID, faberToken) :
            DataApi.getDataCDRDetailedRecords(customerICCID, faberToken))
        return getCDRDataDetailsResponse?.data
      },
      {
        onSuccess: (res) => {
          if (res.data) {
            setDetailedRows(res.data)
          } else {
            setDetailedRows([])
          }
        },
        onError: () => {
          setDetailedRows([])
          toast.error('Error getting CDR data')
        },
        enabled: !!breakdownDialogOpen
      }
    )

  /*REFETCHING OF DATA */
  useEffect(() => {
    if (breakdownDialogOpen) {
      refetchDetailCDRData()
    }
  }, [breakdownDialogOpen])

  return (
    <Dialog
      open={breakdownDialogOpen}
      onClose={() => {
        handleDialogClose()
      }}
      fullWidth={true}
      maxWidth='md'
      PaperComponent={PaperComponent}
      aria-labelledby='draggable-dialog-title'
    >
      <DialogTitle
        sx={{
          mt: '4px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        <TextColorFontHeader>Data Usage Breakdown</TextColorFontHeader>
        <IconButton
          onClick={handleDialogClose}
          size='small'
          data-cy='closeDialogBtn'
          sx={{
            mr: '-10px',
            mt: '-10px'
          }}
        >
          <img
            style={{
              width: '20px',
              height: '20px'
            }}
            src={MenuClose}
          />
        </IconButton>
      </DialogTitle>
      <Divider sx={{ width: '100%' }} />
      <DialogContent>
        <MUIDataGrid
          loading={isDetailedCDRLoading}
          getRowId={(row) => `${row.CDRID}-index${detailedRows.indexOf(row)}`}
          columns={usageColumns}
          rows={detailedRows}
          rowHeight={30}
          disableColumnFilter
          disableColumnMenu
          disableSelectionOnClick
          pagination
          sx={{
            '& .MuiDataGrid-iconSeparator': {
              visibility: 'hidden'
            },
            '& .MuiDataGrid-footerContainer': {
              border: '0'
            },
            '& .MuiDataGrid-cellContent': {
              fontSize: '14px'
            },
            '& .MuiDataGrid-columnHeaderTitle': {
              fontSize: '14px',
              fontWeight: '500'
            },
            '& .MuiDataGrid-cell': {
              border: '0px'
            },
            '& .MuiDataGrid-columnHeaders': {
              border: '0px'
            },
            '&.MuiDataGrid-root .MuiDataGrid-cell:focus': {
              outline: 'none'
            },
            mx: '-10px'
          }}
        />
      </DialogContent>

      <DialogContent sx={{ width: '100%', padding: '0' }}></DialogContent>
    </Dialog>
  )
}

export default CustomerUsageCDRBreakdown

interface DataCDRProps {
  handleDialogClose: () => void
  breakdownDialogOpen: boolean
  customerICCID: string
  faberToken: string
  isExternal: boolean
}
